import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Checkbox, Menu, Modal, Portal, Switch, Text, TextInput } from 'react-native-paper';
import { HAMBURGER_ICONS } from '../../../constants/hamburgerIcons';
import { colors, spacing, typography } from '../../../constants/theme';
import { ItemForm, ReviewCategory } from '../AdminDashboard.types';

interface AdminCreateItemModalProps {
  visible: boolean;
  form: ItemForm;
  categories: ReviewCategory[];
  categoryMenuVisible: boolean;
  selectedTemplateId: number | null;
  onChangeForm: (form: ItemForm) => void;
  onChangeCategoryMenuVisible: (visible: boolean) => void;
  onDismiss: () => void;
  onSubmit: () => void;
}

export const AdminCreateItemModal: React.FC<AdminCreateItemModalProps> = ({
  visible,
  form,
  categories,
  categoryMenuVisible,
  selectedTemplateId,
  onChangeForm,
  onChangeCategoryMenuVisible,
  onDismiss,
  onSubmit,
}) => {
  const parentCategory = categories.find(category => category.id.toString() === form.categoryId);
  const categoryInHamburger = parentCategory?.showInHamburger || false;

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modal}
      >
        <ScrollView>
          <Text style={styles.modalTitle}>Create New Item</Text>

          <View style={styles.formContainer}>
            <TextInput
              label="Name *"
              value={form.name}
              onChangeText={(text) => onChangeForm({ ...form, name: text })}
              style={styles.formField}
            />

            <TextInput
              label="Description"
              value={form.description}
              onChangeText={(text) => onChangeForm({ ...form, description: text })}
              multiline
              style={styles.formField}
            />

            <Text>Category *</Text>
            <Menu
              visible={categoryMenuVisible}
              onDismiss={() => onChangeCategoryMenuVisible(false)}
              anchor={
                <Button
                  mode="outlined"
                  onPress={() => onChangeCategoryMenuVisible(true)}
                  style={styles.formField}
                >
                  {form.categoryId
                    ? categories.find(category => category.id.toString() === form.categoryId)?.name || 'Select Category'
                    : 'Select Category'
                  }
                </Button>
              }
            >
              {categories.filter(category => category.templateId === selectedTemplateId).map(category => (
                <Menu.Item
                  key={category.id}
                  title={category.name}
                  onPress={() => {
                    onChangeForm({ ...form, categoryId: category.id.toString() });
                    onChangeCategoryMenuVisible(false);
                  }}
                />
              ))}
            </Menu>

            <View style={styles.checkboxSection}>
              <Text style={styles.checkboxSectionTitle}>Field Requirements</Text>

              <View style={styles.checkboxRow}>
                <Checkbox
                  status={form.requiresLocation ? 'checked' : 'unchecked'}
                  onPress={() => onChangeForm({
                    ...form,
                    requiresLocation: !form.requiresLocation,
                  })}
                />
                <Text style={styles.checkboxLabel}>Requires Location/Room</Text>
              </View>

              <View style={styles.checkboxRow}>
                <Checkbox
                  status={form.requiresFloor ? 'checked' : 'unchecked'}
                  onPress={() => onChangeForm({
                    ...form,
                    requiresFloor: !form.requiresFloor,
                  })}
                />
                <Text style={styles.checkboxLabel}>Requires Floor</Text>
              </View>
            </View>

            <View style={styles.hamburgerSettings}>
              {categoryInHamburger && (
                <Text style={styles.helperText}>
                  ℹ️ In hamburger via category - select item icon below
                </Text>
              )}
              <View style={styles.switchRow}>
                <Switch
                  value={form.showInHamburger}
                  onValueChange={(value) => onChangeForm({ ...form, showInHamburger: value })}
                />
                <Text style={styles.switchLabel}>
                  Show in Hamburger Menu
                </Text>
              </View>
              {(form.showInHamburger || categoryInHamburger) && (
                <>
                  <Text style={styles.iconLabel}>Hamburger Icon</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={styles.iconGrid}>
                      {HAMBURGER_ICONS.map(iconOption => (
                        <Button
                          key={iconOption.value}
                          mode={form.hamburgerIcon === iconOption.value ? 'contained' : 'outlined'}
                          onPress={() => onChangeForm({ ...form, hamburgerIcon: iconOption.value })}
                          style={styles.iconButton}
                        >
                          {iconOption.label}
                        </Button>
                      ))}
                    </View>
                  </ScrollView>
                  <TextInput
                    label="Display Order (lower appears first)"
                    value={form.hamburgerOrder?.toString() || '0'}
                    onChangeText={(text) => {
                      const num = parseInt(text, 10) || 0;
                      onChangeForm({ ...form, hamburgerOrder: Math.max(0, num) });
                    }}
                    keyboardType="numeric"
                    style={styles.displayOrderField}
                  />
                </>
              )}
            </View>
          </View>

          <View style={styles.formRow}>
            <Button
              mode="outlined"
              onPress={onDismiss}
              style={styles.formField}
            >
              Cancel
            </Button>
            <Button
              mode="contained"
              onPress={onSubmit}
              style={styles.formField}
            >
              Create
            </Button>
          </View>
        </ScrollView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
    minHeight: '70%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
  },
  formContainer: {
    gap: spacing.md,
  },
  formField: {
    flex: 1,
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: spacing.sm,
  },
  checkboxSection: {
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
  checkboxSectionTitle: {
    ...typography.body,
    fontWeight: '600',
    marginBottom: spacing.sm,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  checkboxLabel: {
    marginLeft: spacing.sm,
    flex: 1,
  },
  hamburgerSettings: {
    marginTop: spacing.md,
  },
  helperText: {
    color: colors.primary,
    fontSize: 12,
    marginBottom: spacing.sm,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  switchLabel: {
    marginLeft: spacing.sm,
  },
  iconLabel: {
    marginBottom: spacing.xs,
  },
  iconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  iconButton: {
    minWidth: 100,
    marginBottom: spacing.xs,
  },
  displayOrderField: {
    marginTop: spacing.sm,
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
});

export default AdminCreateItemModal;
